/**
 * The studio: one relief on a board, on paper, under a key light, seen through a
 * hundred-millimetre lens.
 *
 * The scene used to contain no lights at all — every pixel came out of one
 * hand-written shader with a fixed ambient term — and nothing in it cast a shadow.
 * Now there is a pre-filtered environment, a directional key with a real shadow
 * map, a surface that receives it, and a compositor standing in for the camera
 * back. The relief itself streams in: the boundary arrives first and the board
 * appears on the paper, then the terrain, then the population as the raster
 * decodes, each stage the same fields at a different resolution.
 *
 * World space is the normalised equal-area projection and +Z is up.
 */

import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

import {
  APERTURE,
  CAMERAS,
  ENVIRONMENT_INTENSITY,
  EXPOSURE,
  KEY_INTENSITY,
  LIGHT,
  TAA_SAMPLES,
  sunDirection,
} from "../../shared/constants.ts";
import type { PlacedCity, ReliefFields, Ring, View } from "../../shared/types.ts";
import { createCompositor } from "./compositor.ts";
import { buildStudioEnvironment } from "./environment.ts";
import { buildPaletteTextures } from "./palette.ts";
import {
  framingRings,
  lidGeometry,
  mipSampler,
  plateMaterial,
  PLATE_BOTTOM,
  rimGeometry,
  tableMesh,
} from "./plate.ts";
import {
  layoutCityLabels,
  paintPoster,
  type PosterLabel,
  type PosterOverlay,
  type ProjectedCity,
} from "./poster.ts";
import { packRelief, reliefGeometry, reliefLevel, type ReliefUpload } from "./relief.ts";
import { createReliefMaterials } from "./reliefMaterial.ts";
import { bakeVisibility, type BakedVisibility } from "./visibility.ts";

/** Vertices the screen is worth; zoom raises it, an export raises it further. */
const MIN_VERTEX_BUDGET = 220_000;
const SCREEN_VERTEX_CAP = 2_400_000;
const EXPORT_VERTEX_CAP = 8_000_000;

/** Mild perspective: 22° vertical on 35 mm is a 100 mm short telephoto. */
const CAMERA_FOV = 22;
/** Fraction of the frame the country is fitted into. */
const FRAME_FILL = 0.92;
const FRAME_VERTICAL_SHIFT = 0.09;
const ZOOM_IN = 0.4;
const ZOOM_OUT = 3;

const ORBIT_TWEEN_MS = 600;
const POLAR_MARGIN = 0.05;
const TWO_PI = Math.PI * 2;

/**
 * Temporal accumulation is only worth its latency once the camera is at rest, and
 * an export runs it to completion, so the interactive budget is capped well below
 * the export's and yields to the event loop.
 */
const EXPORT_YIELD_EVERY = 4;

export type LabelPosition = PosterLabel;

export interface SceneHandle {
  /** The silhouette, before any relief exists: board, paper, provisional framing. */
  setOutline(rings: Ring[], view: View, titleVisible: boolean): void;
  setCities(cities: PlacedCity[]): void;
  /** Replace the relief. Disposes what the previous fields owned. */
  setRelief(fields: ReliefFields): void;
  goTo(view: View): void;
  onLabels(callback: (labels: LabelPosition[]) => void): void;
  /** Aperture as a fraction of the frame height; 0 turns defocus off. */
  setAperture(aperture: number): void;
  /** Exposure in stops-equivalent multiplier, applied on the tone curve. */
  setExposure(exposure: number): void;
  /**
   * Move the key light. The studio probe is rebuilt with it, so reflections and
   * shadows keep agreeing — which is the whole reason the light can move at all.
   */
  setSun(azimuthDeg: number, elevationDeg: number): void;
  /** Full-resolution frame plus the editorial overlay, run to convergence. */
  snapshot(
    width: number,
    height: number,
    overlay: PosterOverlay,
    onProgress?: (fraction: number) => void,
  ): Promise<Blob>;
  resize(): void;
  dispose(): void;
}

/** Everything the current fields own, so replacing them frees all of it. */
interface Loaded {
  fields: ReliefFields;
  upload: ReliefUpload;
  baked: BakedVisibility;
  surface: THREE.Mesh;
  rim: THREE.Mesh;
  level: number;
  framedDistance: number;
}

interface Tween {
  fromTheta: number;
  toTheta: number;
  fromPhi: number;
  toPhi: number;
  /**
   * Distance travels too. A preset used to inherit whatever distance the previous
   * one had been fitted at, so flying from the atlas view to the perspective one
   * left the country adrift in a third of the frame. The user's own zoom survives
   * as a ratio of the fitted distance rather than as an absolute.
   */
  fromDistance: number;
  toDistance: number;
  fromTarget: THREE.Vector3;
  toTarget: THREE.Vector3;
  start: number;
}

function clamp(value: number, low: number, high: number): number {
  return value < low ? low : value > high ? high : value;
}

export function createScene(canvas: HTMLCanvasElement): SceneHandle {
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: false,
    preserveDrawingBuffer: false,
  });
  // Temporal accumulation resolves edges far past what extra device pixels buy,
  // and every one of them costs a full pass of a heavy fragment shader.
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
  // The tone curve is the compositor's last step, in linear light, once. Leaving it
  // on here would apply it to the scene and then again to the composite.
  renderer.toneMapping = THREE.NoToneMapping;
  renderer.toneMappingExposure = EXPOSURE;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  // The height chain is R32F and read with a linear filter.
  renderer.getContext().getExtension("OES_texture_float_linear");
  const anisotropy = renderer.capabilities.getMaxAnisotropy();

  const scene = new THREE.Scene();
  let sunVector: [number, number, number] = [LIGHT[0], LIGHT[1], LIGHT[2]];
  let environment = buildStudioEnvironment(renderer, sunVector);
  scene.environment = environment;
  scene.environmentIntensity = ENVIRONMENT_INTENSITY;
  scene.background = null;

  const sun = new THREE.DirectionalLight(0xffffff, KEY_INTENSITY);
  sun.color.setRGB(1.0, 0.97, 0.92, THREE.LinearSRGBColorSpace);
  sun.castShadow = true;
  sun.shadow.mapSize.set(4096, 4096);
  sun.shadow.bias = -0.00015;
  sun.shadow.normalBias = 0.0006;
  scene.add(sun);
  scene.add(sun.target);

  const table = tableMesh();
  scene.add(table);

  const camera = new THREE.PerspectiveCamera(CAMERA_FOV, 1, 0.01, 100);
  camera.up.set(0, 0, 1);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  // Panning would slide the poster out of frame with nothing to bring it back.
  controls.enablePan = false;
  controls.minPolarAngle = POLAR_MARGIN;
  controls.maxPolarAngle = Math.PI / 2 - POLAR_MARGIN;

  const palettes = buildPaletteTextures();
  const relief = createReliefMaterials(palettes);
  const plate = plateMaterial();
  const compositor = createCompositor(renderer);
  /**
   * Half-diagonal of the current subject, which the aperture is measured against
   * and the shadow camera is fitted to.
   */
  let sceneRadius = 0.6;

  let current: Loaded | null = null;
  let rings: Ring[] = [];
  /** The subset the camera fit measures; see `framingRings`. */
  let framedRings: Ring[] = [];
  /** Which preset the camera was last flown to; the framing and filename read it. */
  let currentView: View = "top";
  let outline: THREE.Mesh[] = [];
  let titleVisible = false;
  let framed = false;
  let aperture = APERTURE;
  let tween: Tween | null = null;
  let dirty = true;
  let frameHandle = 0;

  let labelSink: ((labels: LabelPosition[]) => void) | null = null;
  let cities: PlacedCity[] = [];
  let projectedCities: ProjectedCity[] = [];
  /** Interleaved city anchors, `x, y, z` per label. */
  let anchors = new Float32Array(0);

  const point = new THREE.Vector3();
  const measureCanvas = document.createElement("canvas");
  const context = measureCanvas.getContext("2d");
  if (!context) throw new Error("Canvas 2D non disponibile per le etichette.");
  const measureContext: CanvasRenderingContext2D = context;

  controls.addEventListener("start", () => {
    tween = null;
  });
  controls.addEventListener("end", () => refreshLod());

  function clearRelief(): void {
    if (!current) return;
    scene.remove(current.surface);
    scene.remove(current.rim);
    current.surface.geometry.dispose();
    current.rim.geometry.dispose();
    current.upload.dispose();
    current.baked.dispose();
    current = null;
  }

  function clearOutline(): void {
    for (const mesh of outline) {
      scene.remove(mesh);
      mesh.geometry.dispose();
    }
    outline = [];
  }

  /** Where a preset puts the camera, as a unit offset from the target. */
  function presetOffset(view: View): THREE.Vector3 {
    const azimuth = THREE.MathUtils.degToRad(CAMERAS[view].azimuthDeg);
    const elevation = THREE.MathUtils.degToRad(CAMERAS[view].elevationDeg);
    return new THREE.Vector3(
      -Math.sin(azimuth) * Math.cos(elevation),
      -Math.cos(azimuth) * Math.cos(elevation),
      Math.sin(elevation),
    ).normalize();
  }

  /**
   * Where a preset should aim, including the title safe area.
   *
   * The shift is applied before the fit rather than after, so the fit accounts for
   * it instead of being invalidated by it.
   */
  function presetTarget(
    offset: THREE.Vector3,
    extent: readonly [number, number, number, number],
    top: number,
    radius: number,
  ): THREE.Vector3 {
    // Screen basis for this preset. Looking straight down would leave `right`
    // undefined; the polar clamp keeps the presets away from it, but a degenerate
    // basis would still poison the fit.
    const forward = offset.clone().negate();
    const right = new THREE.Vector3().crossVectors(forward, new THREE.Vector3(0, 0, 1));
    if (right.lengthSq() < 1e-8) right.set(1, 0, 0);
    right.normalize();
    const up = new THREE.Vector3().crossVectors(right, forward).normalize();
    const [minX, maxX, minY, maxY] = extent;
    return new THREE.Vector3((minX + maxX) * 0.5, (minY + maxY) * 0.5, top * 0.35)
      .addScaledVector(up, radius * FRAME_VERTICAL_SHIFT);
  }

  /**
   * Distance at which the country just fills the frame, by measuring where its own
   * outline actually lands and closing on it.
   *
   * Measuring the *extent box* instead is what left the perspective preset half
   * empty: rotated fifty-two degrees, the box's diagonal sets the projected width
   * while the country inside it occupies a fraction of that. The rings are the real
   * silhouette, and since displacement is purely vertical they bound the horizontal
   * extent exactly; taking them at the underside and at the relief's reach bounds
   * the vertical extent too, because a tilted view puts the far edge highest on
   * screen.
   *
   * The previous fit reasoned about half-widths along the camera's axes and then
   * pushed the camera back by the full depth of the box to be safe. Between those
   * two approximations it left the country occupying barely a third of the poster,
   * which is the sea of empty paper in every earlier capture.
   *
   * The camera is scribbled on while iterating; every caller commits afterwards.
   */
  function fitDistance(
    offset: THREE.Vector3,
    target: THREE.Vector3,
    outline: ReadonlyArray<Ring>,
    top: number,
    radius: number,
  ): number {
    let distance = radius * 3;
    const probe = new THREE.Vector3();
    for (let pass = 0; pass < 4; pass += 1) {
      camera.position.copy(target).addScaledVector(offset, distance);
      camera.near = Math.max(distance * 0.02, 1e-3);
      camera.far = distance + radius * 4;
      camera.lookAt(target);
      camera.updateMatrixWorld();
      camera.updateProjectionMatrix();

      let fill = 0;
      for (const ring of outline) {
        for (let index = 0; index < ring.length; index += 2) {
          const x = ring[index];
          const y = ring[index + 1];
          probe.set(x, y, PLATE_BOTTOM).project(camera);
          fill = Math.max(fill, Math.abs(probe.x), Math.abs(probe.y));
          probe.set(x, y, top).project(camera);
          fill = Math.max(fill, Math.abs(probe.x), Math.abs(probe.y));
        }
      }
      if (!(fill > 0)) break;
      distance *= fill / FRAME_FILL;
    }
    return distance;
  }

  function frameCountry(
    view: View,
    extent: readonly [number, number, number, number],
    top: number,
  ): number {
    const offset = presetOffset(view);
    const radius = Math.hypot(extent[1] - extent[0], extent[3] - extent[2]) * 0.5 + Math.abs(top);
    const target = presetTarget(offset, extent, top, radius);
    const distance = fitDistance(offset, target, framedRings, top, radius);

    controls.target.copy(target);
    controls.minDistance = distance * ZOOM_IN;
    controls.maxDistance = distance * ZOOM_OUT;
    camera.position.copy(target).addScaledVector(offset, distance);
    camera.near = Math.max(distance * 0.02, 1e-3);
    camera.far = distance + radius * 4;
    camera.updateProjectionMatrix();
    controls.update();
    return distance;
  }

  /**
   * The shadow camera has to enclose the object, or the shadow ends mid-paper.
   * Remembered so moving the sun can re-aim without waiting for new fields.
   */
  let litExtent: readonly [number, number, number, number] = [-0.5, 0.5, -0.5, 0.5];
  let litTop = 0;

  function aimSun(extent: readonly [number, number, number, number], top: number): void {
    litExtent = extent;
    litTop = top;
    const [minX, maxX, minY, maxY] = extent;
    const centreX = (minX + maxX) * 0.5;
    const centreY = (minY + maxY) * 0.5;
    const radius = Math.hypot(maxX - minX, maxY - minY) * 0.5 + Math.abs(top) + 0.05;
    // Also the depth over which the lens is allowed to go out of focus, so the
    // aperture means "how much of the object is sharp" rather than an arbitrary
    // number of world units — which is why the same setting used to be invisible
    // from above and overwhelming in perspective.
    sceneRadius = radius;
    sun.target.position.set(centreX, centreY, 0);
    const distance = radius * 4;
    sun.position.set(
      centreX + sunVector[0] * distance,
      centreY + sunVector[1] * distance,
      sunVector[2] * distance,
    );
    const shadow = sun.shadow.camera;
    shadow.left = -radius;
    shadow.right = radius;
    shadow.top = radius;
    shadow.bottom = -radius;
    shadow.near = 0.01;
    shadow.far = distance * 2;
    shadow.updateProjectionMatrix();
  }

  function setOutline(nextRings: Ring[], view: View, showTitle: boolean): void {
    clearRelief();
    clearOutline();
    rings = nextRings;
    framedRings = framingRings(nextRings);
    titleVisible = showTitle;
    framed = false;

    const wall = rimGeometry(rings, () => 0);
    wall.computeBoundingBox();
    const box = wall.boundingBox;
    const extent: [number, number, number, number] = box
      ? [box.min.x, box.max.x, box.min.y, box.max.y]
      : [-0.5, 0.5, -0.5, 0.5];
    for (const geometry of [wall, lidGeometry(rings)]) {
      const mesh = new THREE.Mesh(geometry, plate);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      outline.push(mesh);
      scene.add(mesh);
    }

    aimSun(extent, 0);
    frameCountry(view, extent, 0);
    dirty = true;
  }

  function setRelief(fields: ReliefFields): void {
    clearRelief();
    clearOutline();

    const upload = packRelief(fields, anisotropy);
    relief.setField(fields, upload.coastRange);
    const baked = bakeVisibility(renderer, palettes, fields, upload.height, upload.shade);
    relief.setTextures({
      height: upload.height,
      shade: upload.shade,
      visibility: baked.visibility,
      bounce: baked.bounce,
    });

    const level = reliefLevel(fields, screenVertexBudget(1));
    relief.setLod(level);
    const surface = new THREE.Mesh(reliefGeometry(fields, level), relief.surface);
    surface.customDepthMaterial = relief.depth;
    surface.castShadow = true;
    surface.receiveShadow = true;
    surface.frustumCulled = false;
    scene.add(surface);

    const rim = new THREE.Mesh(rimGeometry(rings, mipSampler(fields, level)), plate);
    rim.castShadow = true;
    rim.receiveShadow = true;
    scene.add(rim);

    aimSun(fields.extent, fields.peak);
    const framedDistance = framed
      ? camera.position.distanceTo(controls.target)
      : frameCountry(currentView, fields.extent, fields.reach);
    framed = true;

    current = { fields, upload, baked, surface, rim, level, framedDistance };
    placeLabels();
    dirty = true;
  }

  /** City anchors ride the surface, so a leader always starts on the relief. */
  function placeLabels(): void {
    projectedCities = new Array<ProjectedCity>(cities.length);
    anchors = new Float32Array(cities.length * 3);
    const fields = current?.fields ?? null;
    for (let index = 0; index < cities.length; index += 1) {
      const city = cities[index];
      const target = index * 3;
      anchors[target] = city.x;
      anchors[target + 1] = city.y;
      anchors[target + 2] = fields ? heightAtPoint(fields, city.x, city.y) : 0;
      projectedCities[index] = {
        name: city.name,
        anchorX: 0,
        anchorY: 0,
        population: city.population,
        visible: false,
      };
    }
  }

  function heightAtPoint(fields: ReliefFields, x: number, y: number): number {
    const [minX, maxX, minY, maxY] = fields.extent;
    const column = clamp(
      Math.floor(((x - minX) / (maxX - minX)) * fields.width),
      0,
      fields.width - 1,
    );
    const row = clamp(
      Math.floor(((maxY - y) / (maxY - minY)) * fields.height),
      0,
      fields.height - 1,
    );
    return fields.heightMips[0].data[row * fields.width + column];
  }

  function layoutLabels(width: number, height: number, showTitle = titleVisible): PosterLabel[] {
    const raw = new Array<ProjectedCity>(projectedCities.length);
    for (let index = 0; index < projectedCities.length; index += 1) {
      const city = projectedCities[index];
      const target = index * 3;
      point.set(anchors[target], anchors[target + 1], anchors[target + 2]);
      point.applyMatrix4(camera.matrixWorldInverse);
      const behind = point.z > -camera.near;
      point.applyMatrix4(camera.projectionMatrix);
      raw[index] = {
        ...city,
        anchorX: (point.x * 0.5 + 0.5) * width,
        anchorY: (0.5 - point.y * 0.5) * height,
        visible: !behind && point.x >= -1 && point.x <= 1 && point.y >= -1 && point.y <= 1,
      };
    }
    return layoutCityLabels(raw, width, height, showTitle, measureContext);
  }

  function emitLabels(): void {
    if (!labelSink) return;
    labelSink(layoutLabels(canvas.clientWidth, canvas.clientHeight));
  }

  function screenVertexBudget(zoom: number): number {
    const pixels = Math.max(canvas.clientWidth, 1) * Math.max(canvas.clientHeight, 1);
    return Math.round(
      clamp(pixels * 1.6 * zoom * zoom, MIN_VERTEX_BUDGET, SCREEN_VERTEX_CAP),
    );
  }

  function applyLevel(loaded: Loaded, level: number): void {
    if (level === loaded.level) return;
    const previousSurface = loaded.surface.geometry;
    const previousRim = loaded.rim.geometry;
    loaded.surface.geometry = reliefGeometry(loaded.fields, level);
    // The wall starts where the relief surface now is, which is a different number
    // at a different level.
    loaded.rim.geometry = rimGeometry(rings, mipSampler(loaded.fields, level));
    previousSurface.dispose();
    previousRim.dispose();
    loaded.level = level;
    relief.setLod(level);
    dirty = true;
  }

  function refreshLod(): void {
    if (!current) return;
    const distance = camera.position.distanceTo(controls.target);
    const zoom = current.framedDistance / Math.max(distance, 1e-6);
    applyLevel(current, reliefLevel(current.fields, screenVertexBudget(zoom)));
  }

  function advanceTween(now: number): boolean {
    if (!tween) return false;
    const progress = Math.min((now - tween.start) / ORBIT_TWEEN_MS, 1);
    const eased =
      progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) * 0.5;
    const theta = tween.fromTheta + (tween.toTheta - tween.fromTheta) * eased;
    const phi = tween.fromPhi + (tween.toPhi - tween.fromPhi) * eased;
    const distance = tween.fromDistance + (tween.toDistance - tween.fromDistance) * eased;
    const ground = Math.sin(phi) * distance;
    controls.target.lerpVectors(tween.fromTarget, tween.toTarget, eased);
    camera.position.set(
      controls.target.x + ground * Math.sin(theta),
      controls.target.y - ground * Math.cos(theta),
      controls.target.z + Math.cos(phi) * distance,
    );
    if (progress === 1) tween = null;
    return true;
  }

  function renderSample(): void {
    compositor.jitter(camera);
    renderer.setRenderTarget(compositor.sceneTarget);
    renderer.render(scene, camera);
    camera.clearViewOffset();
    compositor.accumulate();
    compositor.present(null, {
      focus: camera.position.distanceTo(controls.target),
      aperture,
      depthRange: sceneRadius,
      camera,
    });
  }

  function tick(now: number): void {
    frameHandle = requestAnimationFrame(tick);
    const tweening = advanceTween(now);
    const moved = controls.update();
    if (dirty || moved || tweening) {
      compositor.restart();
      dirty = false;
    } else if (compositor.converged) {
      return;
    }
    renderSample();
    emitLabels();
  }

  function resize(): void {
    const width = Math.max(canvas.clientWidth, 1);
    const height = Math.max(canvas.clientHeight, 1);
    const ratio = Math.min(window.devicePixelRatio, 1.5);
    renderer.setPixelRatio(ratio);
    renderer.setSize(width, height, false);
    compositor.setSize(width * ratio, height * ratio);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    refreshLod();
    dirty = true;
  }

  resize();
  frameHandle = requestAnimationFrame(tick);

  return {
    setOutline(nextRings: Ring[], view: View, showTitle: boolean): void {
      currentView = view;
      setOutline(nextRings, view, showTitle);
    },

    setCities(nextCities: PlacedCity[]): void {
      cities = nextCities;
      placeLabels();
      dirty = true;
    },

    setRelief,

    setAperture(next: number): void {
      aperture = next;
      dirty = true;
    },

    setExposure(exposure: number): void {
      renderer.toneMappingExposure = exposure;
      dirty = true;
    },

    setSun(azimuthDeg: number, elevationDeg: number): void {
      sunVector = sunDirection(azimuthDeg, elevationDeg);
      environment.dispose();
      environment = buildStudioEnvironment(renderer, sunVector);
      scene.environment = environment;
      aimSun(litExtent, litTop);
      dirty = true;
    },

    goTo(view: View): void {
      currentView = view;
      const preset = CAMERAS[view];
      const fromTheta = controls.getAzimuthalAngle();
      const target = -THREE.MathUtils.degToRad(preset.azimuthDeg);
      // Shortest way round, so a 350° turn is not spun the long way.
      let delta = (target - fromTheta) % TWO_PI;
      if (delta > Math.PI) delta -= TWO_PI;
      if (delta < -Math.PI) delta += TWO_PI;
      // The preset owns the target and the distance too. The user's own zoom
      // survives as a ratio of whatever the preset fits to, read *before* fitting:
      // `fitDistance` scribbles on the camera, so asking afterwards would return
      // the fit itself and the ratio would square.
      const held = camera.position.distanceTo(controls.target);
      const zoom = current && current.framedDistance > 0 ? held / current.framedDistance : 1;
      const offset = presetOffset(view);
      const aim = current
        ? presetTarget(offset, current.fields.extent, current.fields.reach, sceneRadius)
        : controls.target.clone();
      const fitted = current
        ? fitDistance(offset, aim, framedRings, current.fields.reach, sceneRadius)
        : held;
      if (current) current.framedDistance = fitted;
      controls.minDistance = fitted * ZOOM_IN;
      controls.maxDistance = fitted * ZOOM_OUT;
      tween = {
        fromTheta,
        toTheta: fromTheta + delta,
        fromPhi: controls.getPolarAngle(),
        toPhi: clamp(
          Math.PI / 2 - THREE.MathUtils.degToRad(preset.elevationDeg),
          controls.minPolarAngle,
          controls.maxPolarAngle,
        ),
        fromDistance: held,
        toDistance: clamp(fitted * zoom, controls.minDistance, controls.maxDistance),
        fromTarget: controls.target.clone(),
        toTarget: aim,
        start: performance.now(),
      };
      dirty = true;
    },

    onLabels(callback: (labels: LabelPosition[]) => void): void {
      labelSink = callback;
      dirty = true;
    },

    async snapshot(
      width: number,
      height: number,
      overlay: PosterOverlay,
      onProgress?: (fraction: number) => void,
    ): Promise<Blob> {
      if (!current) throw new Error("Renderizza uno Stato prima di esportare.");
      const loaded = current;
      const originalAspect = camera.aspect;
      const originalLevel = loaded.level;

      const target = new THREE.WebGLRenderTarget(width, height, {
        type: THREE.UnsignedByteType,
        format: THREE.RGBAFormat,
        depthBuffer: false,
        stencilBuffer: false,
        generateMipmaps: false,
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
      });
      target.texture.colorSpace = THREE.SRGBColorSpace;
      const pixels = new Uint8Array(width * height * 4);

      try {
        applyLevel(loaded, reliefLevel(loaded.fields, Math.min(width * height * 2, EXPORT_VERTEX_CAP)));
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        compositor.setSize(width, height);
        compositor.restart();

        const focus = camera.position.distanceTo(controls.target);
        // Run the accumulation all the way out. Yielding keeps the tab alive and
        // lets the progress line move rather than freezing for several seconds.
        for (let sample = 0; !compositor.converged; sample += 1) {
          compositor.jitter(camera);
          renderer.setRenderTarget(compositor.sceneTarget);
          renderer.render(scene, camera);
          camera.clearViewOffset();
          compositor.accumulate();
          if (sample % EXPORT_YIELD_EVERY === 0) {
            onProgress?.(sample / TAA_SAMPLES);
            await yieldToBrowser();
          }
        }
        compositor.present(target, { focus, aperture, depthRange: sceneRadius, camera });
        renderer.readRenderTargetPixels(target, 0, 0, width, height, pixels);
        flipRows(pixels, width, height);

        const output = document.createElement("canvas");
        output.width = width;
        output.height = height;
        const exportContext = output.getContext("2d");
        if (!exportContext) throw new Error("Canvas 2D non disponibile per l'export.");
        exportContext.putImageData(
          new ImageData(new Uint8ClampedArray(pixels.buffer), width, height),
          0,
          0,
        );
        paintPoster(
          exportContext,
          width,
          height,
          overlay,
          layoutLabels(width, height, overlay.title),
        );
        onProgress?.(1);
        return await canvasBlob(output);
      } finally {
        target.dispose();
        applyLevel(loaded, originalLevel);
        camera.aspect = originalAspect;
        camera.updateProjectionMatrix();
        resize();
      }
    },

    resize,

    dispose(): void {
      cancelAnimationFrame(frameHandle);
      clearRelief();
      clearOutline();
      controls.dispose();
      compositor.dispose();
      relief.dispose();
      plate.dispose();
      table.geometry.dispose();
      if (table.material instanceof THREE.Material) table.material.dispose();
      palettes.terrain.dispose();
      palettes.population.dispose();
      environment.dispose();
      renderer.dispose();
      labelSink = null;
      cities = [];
      projectedCities = [];
      anchors = new Float32Array(0);
    },
  };
}

/** WebGL readback is bottom-up; swap rows in place before constructing ImageData. */
function flipRows(pixels: Uint8Array, width: number, height: number): void {
  const stride = width * 4;
  const row = new Uint8Array(stride);
  for (let top = 0; top < Math.floor(height / 2); top += 1) {
    const bottom = height - top - 1;
    const topOffset = top * stride;
    const bottomOffset = bottom * stride;
    row.set(pixels.subarray(topOffset, topOffset + stride));
    pixels.copyWithin(topOffset, bottomOffset, bottomOffset + stride);
    pixels.set(row, bottomOffset);
  }
}

function yieldToBrowser(): Promise<void> {
  const { promise, resolve } = Promise.withResolvers<void>();
  setTimeout(resolve, 0);
  return promise;
}

function canvasBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  const { promise, resolve, reject } = Promise.withResolvers<Blob>();
  canvas.toBlob((result) => {
    if (result) resolve(result);
    else reject(new Error("Il compositore non ha prodotto un PNG."));
  }, "image/png");
  return promise;
}
